import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Colores del tema
const NAVY   = [0, 43, 91];
const WHITE  = [255, 255, 255];
const LIGHT_BLUE_ROW = [232, 240, 253];
const GREY_TEXT = [80, 80, 80];

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  if (dateStr.includes('-')) {
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  }
  return dateStr;
};

const formatToday = () => {
  const now = new Date();
  const d = String(now.getDate()).padStart(2, '0');
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${d}/${m}/${now.getFullYear()}`;
};

const formatUSD = (value) =>
  value != null
    ? `USD ${Number(value).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '-';

// Escribe un párrafo con segmentos de texto con distintos estilos en la misma línea.
// segments: [{ text, bold, fontSize }]
// Devuelve el nuevo yPos tras el texto.
const drawMixedLine = (doc, segments, x, y, maxWidth) => {
  let curX = x;
  const lineHeight = 6.5;
  let lines = [[]];

  // Pre-calcular ancho de cada segmento y hacer word-wrap manual
  segments.forEach((seg) => {
    doc.setFont('helvetica', seg.bold ? 'bold' : 'normal');
    doc.setFontSize(seg.fontSize || 11);
    const words = seg.text.split(' ');
    words.forEach((word, wi) => {
      const piece = wi === 0 ? word : ' ' + word;
      const w = doc.getTextWidth(piece);
      // Chequear si cabe en la línea actual
      const lineWidth = lines[lines.length - 1].reduce((acc, s) => acc + s.w, 0);
      if (lineWidth + w > maxWidth && lineWidth > 0) {
        lines.push([]);
        // agregar sin el espacio inicial
        const trimmed = word;
        doc.setFont('helvetica', seg.bold ? 'bold' : 'normal');
        doc.setFontSize(seg.fontSize || 11);
        const wt = doc.getTextWidth(trimmed);
        lines[lines.length - 1].push({ text: trimmed, bold: seg.bold, fontSize: seg.fontSize || 11, w: wt });
      } else {
        lines[lines.length - 1].push({ text: piece, bold: seg.bold, fontSize: seg.fontSize || 11, w: w });
      }
    });
  });

  let yy = y;
  lines.forEach((line) => {
    let xx = x;
    line.forEach((token) => {
      doc.setFont('helvetica', token.bold ? 'bold' : 'normal');
      doc.setFontSize(token.fontSize);
      doc.setTextColor(0, 0, 0);
      doc.text(token.text, xx, yy);
      xx += token.w;
    });
    yy += lineHeight;
  });

  return yy;
};

const ReciboPagoPDF = {
  generate: (liquidacion) => {
    const doc = new jsPDF('portrait', 'mm', 'a4');
    const pageW  = doc.internal.pageSize.getWidth();
    const pageH  = doc.internal.pageSize.getHeight();
    const margin = 18;
    const contentW = pageW - 2 * margin;
    const today  = formatToday();

    const importadora   = liquidacion.importadora_razon_social || '-';
    const transportista = liquidacion.company_name || liquidacion.company_alias || '-';
    const montoPago     = formatUSD(liquidacion.a_pagar ?? liquidacion.total_usd);
    const tipoPago      = liquidacion.pago_tipo || '-';
    const numeroCheque  = liquidacion.numero_cheque || null;
    const trucks        = liquidacion.trucks || [];

    let y = 0;

    // ── 1. HEADER BAR ───────────────────────────────────────────────
    const headerH = 14;
    doc.setFillColor(...NAVY);
    doc.rect(0, y, pageW, headerH, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.setTextColor(...WHITE);
    doc.text('RECIBO DE PAGO', pageW / 2, y + 9.5, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    y += headerH;

    // ── 2. LÍNEA AZUL IZQUIERDA (decorativa) ────────────────────────
    doc.setDrawColor(...NAVY);
    doc.setLineWidth(1.2);
    doc.line(margin - 5, y + 4, margin - 5, pageH - 15);
    doc.setLineWidth(0.3);

    // ── 3. FECHA EMISIÓN (alineada a la derecha) ─────────────────────
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...GREY_TEXT);
    doc.text(`Fecha emisión: ${today}`, pageW - margin, y, { align: 'right' });
    doc.setTextColor(0, 0, 0);
    y += 20;  // espacio antes de los campos

    // ── 4. NOMBRE ────────────────────────────────────────────────────
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text('Nombre:', margin, y);
    const xAfterNombre = margin + doc.getTextWidth('Nombre:') + 2;
    doc.setLineDashPattern([1.2, 1.2], 0);
    doc.setDrawColor(100, 100, 100);
    doc.line(xAfterNombre, y + 0.8, margin + contentW, y + 0.8);
    doc.setLineDashPattern([], 0);
    y += 8;

    // ── 5. DNI / RUT ─────────────────────────────────────────────────
    doc.text('DNI o RUT N°:', margin, y);
    const xAfterRut = margin + doc.getTextWidth('DNI o RUT N°:') + 2;
    doc.setLineDashPattern([1.2, 1.2], 0);
    doc.setDrawColor(100, 100, 100);
    doc.line(xAfterRut, y + 0.8, margin + contentW * 0.55, y + 0.8);
    doc.setLineDashPattern([], 0);
    y += 14;

    // ── 6. PÁRRAFO PRINCIPAL (texto mixto) ───────────────────────────
    const segments = [
      { text: 'Recibí de ',           bold: false, fontSize: 11 },
      { text: importadora,             bold: true,  fontSize: 11 },
      { text: ' la suma de  ',         bold: false, fontSize: 11 },
      { text: montoPago,               bold: true,  fontSize: 13 },
      { text: '  correspondiente a servicio de transportes efectuados a través de  ', bold: false, fontSize: 11 },
      { text: transportista + ',',     bold: true,  fontSize: 11 },
      { text: '  según el siguiente detalle:', bold: false, fontSize: 11 },
    ];

    y = drawMixedLine(doc, segments, margin, y, contentW);
    y += 10;

    // ── 7. TABLA DE DETALLE ──────────────────────────────────────────
    const hasCheque = !!numeroCheque;

    const headCols = ['FECHA CARGA', 'N° DESPACHO', 'FACTURA TRANSPORTE', 'TIPO PAGO'];
    if (hasCheque) headCols.push('N° CHEQUE');
    headCols.push('CHOFER', 'DESTINO');

    const tableRows = trucks.map((t) => {
      const row = [
        formatDate(t.load_date),
        liquidacion.dispatch_number || '-',
        liquidacion.factura_transporte || '-',
        tipoPago,
      ];
      if (hasCheque) row.push(numeroCheque);
      row.push(t.driver_name || '-', t.destination || '-');
      return row;
    });

    if (tableRows.length === 0) {
      const row = ['-', liquidacion.dispatch_number || '-', liquidacion.factura_transporte || '-', tipoPago];
      if (hasCheque) row.push(numeroCheque);
      row.push('-', '-');
      tableRows.push(row);
    }

    autoTable(doc, {
      head: [headCols],
      body: tableRows,
      startY: y,
      margin: { left: margin, right: margin },
      headStyles: {
        fillColor: NAVY,
        textColor: WHITE,
        fontStyle: 'bold',
        fontSize: 9,
        halign: 'center',
        cellPadding: 4,
      },
      bodyStyles: {
        fontSize: 9,
        halign: 'center',
        cellPadding: 3.5,
        textColor: [30, 30, 30],
      },
      alternateRowStyles: { fillColor: LIGHT_BLUE_ROW },
      tableLineColor: [190, 210, 240],
      tableLineWidth: 0.25,
      styles: { overflow: 'linebreak' },
    });

    y = doc.lastAutoTable.finalY;

    // ── 8. FIRMA (centrada, parte inferior) ─────────────────────────
    const firmaY = Math.max(y + 50, pageH - 40);
    const lineLen = 60;
    const lineX = (pageW - lineLen) / 2;

    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.4);
    doc.line(lineX, firmaY, lineX + lineLen, firmaY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...GREY_TEXT);
    doc.text('Firma Receptor', pageW / 2, firmaY + 5, { align: 'center' });

    // ── 9. PIE DE PÁGINA ─────────────────────────────────────────────
    doc.setFontSize(7);
    doc.setTextColor(170, 170, 170);
    doc.text(
      `Despacho ${liquidacion.dispatch_number || '-'} | ${importadora} | Emitido el ${today}`,
      pageW / 2,
      pageH - 6,
      { align: 'center' }
    );

    // ── Descargar ────────────────────────────────────────────────────
    const safeName = (liquidacion.company_alias || liquidacion.company_name || 'transportista')
      .replace(/\s+/g, '_');
    doc.save(`Recibo_${liquidacion.dispatch_number || 'pago'}_${safeName}.pdf`);
  },
};

export default ReciboPagoPDF;
